(() => {
  'use strict';

  const STORAGE_KEY = 'md_energeez_wishlist';
  const LEGACY_STORAGE_KEY = 'm-wishlist-products';
  const MIGRATION_KEY = 'md_energeez_wishlist_migrated_v1';
  const BUTTON_SELECTOR = '[data-md-wishlist-button], [data-md-product-wishlist], .m-wishlist-button';
  const MD_COUNT_SELECTOR = '[data-md-wishlist-count]';
  const LEGACY_COUNT_SELECTOR = '.m-wishlist-count';
  const t = (key, fallback = '', replacements = {}) => window.MDT ? window.MDT(key, fallback, replacements) : fallback;

  class MDWishlist {
    constructor() {
      this.items = this.readStorage();
      this.migrateLegacyWishlist();
      this.bindEvents();
      this.refresh(document);
      this.renderWishlistPage();
    }

    normalizeItem(item) {
      if (item === null || item === undefined) return null;

      if (typeof item === 'string' || typeof item === 'number') {
        const value = String(item).trim();
        if (!value) return null;

        if (/^\d+$/.test(value)) {
          return { id: value, handle: '', title: '', url: '' };
        }

        return { id: '', handle: value, title: '', url: '' };
      }

      if (typeof item !== 'object') return null;

      const normalized = {
        id: item.id !== undefined && item.id !== null ? String(item.id).trim() : '',
        handle: item.handle ? String(item.handle).trim() : '',
        title: item.title ? String(item.title).trim() : '',
        url: item.url ? String(item.url).trim() : ''
      };

      return normalized.id || normalized.handle ? normalized : null;
    }

    mergeItems(items) {
      const merged = [];

      items.forEach((rawItem) => {
        const item = this.normalizeItem(rawItem);
        if (!item) return;

        const existing = merged.find((candidate) => this.itemsMatch(candidate, item));

        if (existing) {
          existing.id = existing.id || item.id;
          existing.handle = existing.handle || item.handle;
          existing.title = existing.title || item.title;
          existing.url = existing.url || item.url;
        } else {
          merged.push(item);
        }
      });

      return merged;
    }

    readStorage() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(stored) ? this.mergeItems(stored) : [];
      } catch (error) {
        return [];
      }
    }

    writeStorage({ dispatch = true } = {}) {
      this.items = this.mergeItems(this.items);

      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (error) {
        // The wishlist remains usable during the current page view.
      }

      this.updateWishlistCount();
      this.setWishlistButtonsState();

      if (dispatch) {
        window.dispatchEvent(
          new CustomEvent('md:wishlist:change', {
            detail: {
              items: this.items.slice(),
              count: this.items.length
            }
          })
        );
      }
    }

    migrateLegacyWishlist() {
      let alreadyMigrated = false;

      try {
        alreadyMigrated = window.localStorage.getItem(MIGRATION_KEY) === 'true';
      } catch (error) {
        alreadyMigrated = false;
      }

      if (alreadyMigrated) return;

      try {
        const legacy = JSON.parse(window.localStorage.getItem(LEGACY_STORAGE_KEY) || '[]');

        if (Array.isArray(legacy) && legacy.length) {
          const migratedItems = legacy
            .map((handle) => ({ id: '', handle: String(handle || '').trim(), title: '', url: '' }))
            .filter((item) => item.handle);

          this.items = this.mergeItems([...this.items, ...migratedItems]);
          this.writeStorage({ dispatch: false });
        }

        window.localStorage.setItem(MIGRATION_KEY, 'true');
      } catch (error) {
        // Do not block the current wishlist if legacy storage is unavailable.
      }
    }

    itemsMatch(first, second) {
      if (first.id && second.id && String(first.id) === String(second.id)) return true;
      if (first.handle && second.handle && first.handle === second.handle) return true;
      return false;
    }

    productFromButton(button) {
      const card = button.closest('.md-product-card, .m-product-card');
      const titleLink = card
        ? card.querySelector('.md-product-card__title a, .m-product-card__title a, .m-product-card__name a')
        : null;

      return this.normalizeItem({
        id: button.dataset.productId || '',
        handle: button.dataset.productHandle || '',
        title: button.dataset.productTitle || (titleLink ? titleLink.textContent.trim() : ''),
        url: button.dataset.productUrl || (titleLink ? titleLink.getAttribute('href') : '')
      });
    }

    isFavorite(product) {
      if (!product) return false;
      return this.items.some((item) => this.itemsMatch(item, product));
    }

    addToWishlist(productOrHandle) {
      const product = this.normalizeItem(productOrHandle);
      if (!product || this.isFavorite(product)) return;

      this.items.push(product);
      this.writeStorage();
    }

    removeFromWishlist(productOrHandle) {
      const product = this.normalizeItem(productOrHandle);
      if (!product) return;

      this.items = this.items.filter((item) => !this.itemsMatch(item, product));
      this.writeStorage();
    }

    toggleWishlist(button) {
      const product = this.productFromButton(button);
      if (!product) return;

      const wasFavorite = this.isFavorite(product);

      if (wasFavorite) {
        this.items = this.items.filter((item) => !this.itemsMatch(item, product));
      } else {
        this.items.push(product);
      }

      this.writeStorage();

      const wishlistItem = button.closest('[data-md-wishlist-item]');
      if (wasFavorite && wishlistItem) {
        wishlistItem.remove();
        this.syncWishlistPageEmptyState();
      }
    }

    setButtonState(button) {
      const product = this.productFromButton(button);
      if (!product) return;

      const active = this.isFavorite(product);
      const isMdButton = button.matches('[data-md-wishlist-button], [data-md-product-wishlist]');

      button.classList.toggle('is-active', active);
      button.classList.toggle('added-to-wishlist', active);
      button.setAttribute('aria-pressed', String(active));

      if (!button.dataset.mdWishlistDefaultLabel) {
        button.dataset.mdWishlistDefaultLabel =
          button.getAttribute('aria-label') || t('wishlist.add', 'Add product to favorites');
      }

      const productTitle = product.title || 'Product';
      const label = active
        ? t('wishlist.remove', 'Remove {title} from favorites', { title: productTitle })
        : button.dataset.mdWishlistDefaultLabel;

      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);

      const text = button.querySelector('.m-wishlist-button-text');
      if (text) {
        if (!text.dataset.mdWishlistDefaultText) {
          text.dataset.mdWishlistDefaultText = text.textContent.trim();
        }

        const removeText = text.dataset.revertText || t('wishlist.removeShort', 'Remove from favorites');
        text.textContent = active ? removeText : text.dataset.mdWishlistDefaultText;
      }

      if (isMdButton && product.handle && !button.dataset.productHandle) {
        button.dataset.productHandle = product.handle;
      }
    }

    setWishlistButtonsState(scope = document) {
      if (!scope || !scope.querySelectorAll) return;
      scope.querySelectorAll(BUTTON_SELECTOR).forEach((button) => this.setButtonState(button));
    }

    updateWishlistCount() {
      const count = this.items.length;
      const counters = new Set([
        ...document.querySelectorAll(MD_COUNT_SELECTOR),
        ...document.querySelectorAll(LEGACY_COUNT_SELECTOR)
      ]);

      counters.forEach((counter) => {
        counter.textContent = String(count);
        counter.setAttribute('aria-label', t(count === 1 ? 'wishlist.countOne' : 'wishlist.countOther', '{count} favorites', { count }));

        if (counter.matches(MD_COUNT_SELECTOR)) {
          counter.hidden = count < 1;
        }

        if (counter.classList.contains('m-wishlist-count')) {
          counter.classList.toggle('m:hidden', count < 1);
        }
      });

      document.body.classList.toggle('wishlist-has-item', count > 0);
    }

    refresh(scope = document) {
      this.items = this.readStorage();
      this.updateWishlistCount();
      this.setWishlistButtonsState(scope);
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const button = event.target.closest(BUTTON_SELECTOR);
        if (!button) return;

        event.preventDefault();
        this.toggleWishlist(button);
      });

      window.addEventListener('storage', (event) => {
        if (event.key !== STORAGE_KEY) return;
        this.refresh(document);
        this.renderWishlistPage();
      });

      document.addEventListener('shopify:section:load', (event) => {
        this.setWishlistButtonsState(event.target);
        this.updateWishlistCount();
      });

      window.addEventListener('pageshow', () => this.refresh(document));
    }

    syncWishlistPageEmptyState() {
      const root = document.querySelector('[data-md-wishlist-page]');
      if (!root) return;

      const grid = root.querySelector('[data-md-wishlist-grid]');
      const empty = root.querySelector('[data-md-wishlist-empty]');
      const hasCards = Boolean(grid && grid.querySelector('[data-md-wishlist-item]'));

      if (grid) grid.hidden = !hasCards;
      if (empty) empty.hidden = hasCards;
    }

    productCardUrl(handle) {
      const shopRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

      return `${shopRoot}products/${encodeURIComponent(handle)}?view=md-wishlist-card`;
    }

    async fetchWishlistCard(item) {
      if (!item.handle) return null;

      try {
        const response = await fetch(this.productCardUrl(item.handle), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) return null;

        const html = await response.text();
        const template = document.createElement('template');
        template.innerHTML = html.trim();
        const card = template.content.querySelector('.md-product-card');

        if (!card) return null;

        const wrapper = document.createElement('div');
        wrapper.className = 'md-wishlist__item';
        wrapper.setAttribute('data-md-wishlist-item', '');
        wrapper.appendChild(card);

        const button = wrapper.querySelector('[data-md-wishlist-button]');
        const titleLink = wrapper.querySelector('.md-product-card__title a');

        const enriched = this.normalizeItem({
          id: button ? button.dataset.productId : item.id,
          handle: button ? button.dataset.productHandle : item.handle,
          title: button ? button.dataset.productTitle : item.title,
          url: button ? button.dataset.productUrl : (titleLink ? titleLink.getAttribute('href') : item.url)
        });

        return { wrapper, original: item, enriched };
      } catch (error) {
        return null;
      }
    }

    async renderWishlistPage() {
      const root = document.querySelector('[data-md-wishlist-page]');
      if (!root) return;

      const grid = root.querySelector('[data-md-wishlist-grid]');
      const loading = root.querySelector('[data-md-wishlist-loading]');
      const empty = root.querySelector('[data-md-wishlist-empty]');

      if (!grid || !empty) return;

      this.items = this.readStorage();
      grid.innerHTML = '';
      grid.hidden = true;
      empty.hidden = true;
      if (loading) loading.hidden = false;

      if (!this.items.length) {
        if (loading) loading.hidden = true;
        empty.hidden = false;
        return;
      }

      const requestedItems = this.items.slice();
      const results = await Promise.all(requestedItems.map((item) => this.fetchWishlistCard(item)));
      const validResults = results.filter(Boolean);
      const staleItems = requestedItems.filter((item, index) => !results[index]);

      // Verwijder producten die niet meer bestaan direct uit de opgeslagen favorieten.
      if (staleItems.length) {
        this.items = this.items.filter((item) => !staleItems.some((staleItem) => this.itemsMatch(item, staleItem)));
        this.writeStorage({ dispatch: false });
      }

      if (!validResults.length) {
        if (loading) loading.hidden = true;
        empty.hidden = false;
        this.updateWishlistCount();
        return;
      }

      const fragment = document.createDocumentFragment();
      let storageChanged = false;

      validResults.forEach((result) => {
        fragment.appendChild(result.wrapper);

        if (result.enriched) {
          const existing = this.items.find((item) => this.itemsMatch(item, result.original));
          if (existing) {
            const before = JSON.stringify(existing);
            existing.id = existing.id || result.enriched.id;
            existing.handle = existing.handle || result.enriched.handle;
            existing.title = existing.title || result.enriched.title;
            existing.url = existing.url || result.enriched.url;
            storageChanged = storageChanged || before !== JSON.stringify(existing);
          }
        }
      });

      grid.appendChild(fragment);
      grid.hidden = false;
      if (loading) loading.hidden = true;

      if (storageChanged) {
        this.writeStorage({ dispatch: false });
      } else {
        this.setWishlistButtonsState(grid);
        this.updateWishlistCount();
      }
    }
  }

  const wishlist = new MDWishlist();
  window.MinimogTheme = window.MinimogTheme || {};
  window.MinimogTheme.Wishlist = wishlist;
  window.MDEnergeezWishlist = wishlist;
})();
